import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// Kebab (⋮) overflow menu. Reused across every My Trips / Journey surface — trip
// cards, the trip detail header, alternative chips, the journey detail header,
// stop rows and the stop sheet — so per-item actions look and behave identically
// everywhere. See docs/codebase/ui-conventions.md §Overflow / action menus.
//
// The menu is ITEMS-DRIVEN: each caller passes the rows it wants, in the order
// it wants, with an icon from KebabIcons. (It used to hardcode Edit + Delete,
// which is why every surface offered exactly those two actions.)
//
// The panel is rendered in a portal with fixed positioning anchored to the
// trigger. Item lists sit in overflow containers (the horizontal chip scroller;
// a card with overflow:hidden), so an in-flow popover would be clipped — the
// portal escapes that. Colours follow the app tokens (accent #157a55,
// danger #cf5a3c, muted #6c7872).

export type KebabTone = 'accent' | 'danger' | 'muted';

export interface KebabItem {
  label: string;
  /** Pick from KebabIcons so the vocabulary stays consistent across surfaces. */
  icon: React.ReactNode;
  onSelect: () => void;
  /** Defaults to 'accent'. Destructive rows use 'danger'. */
  tone?: KebabTone;
  disabled?: boolean;
  /**
   * Convenience for conditional rows, so callers can build one array instead of
   * filtering: e.g. "Delete other alternatives" is hidden when there is only
   * one alternative. Hidden rows are dropped entirely.
   */
  hidden?: boolean;
}

export interface KebabMenuProps {
  items: KebabItem[];
  disabled?: boolean;
  /** Accessible label for the trigger (context-specific, e.g. "Trip options"). */
  ariaLabel?: string;
}

const svg = (path: React.ReactNode, extra?: { fill?: string }) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill={extra?.fill || 'none'} aria-hidden style={{ flex: 'none' }}>
    {path}
  </svg>
);
const stroke = { stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };

/**
 * The shared icon vocabulary for menu rows. One icon per *meaning*, so the same
 * action reads the same on every surface.
 */
export const KebabIcons = {
  /** Rename */
  pencil: svg(<path d="M14.5 5.5l4 4M3 21l4-1L20 7a2 2 0 0 0-3-3L4 17l-1 4z" {...stroke} />),
  /** Trip preferences (the brief/params behind a trip) */
  sliders: svg(<><path d="M4 6h10M18 6h2M4 12h4M12 12h8M4 18h10M18 18h2" {...stroke} /><circle cx="16" cy="6" r="2" {...stroke} /><circle cx="10" cy="12" r="2" {...stroke} /><circle cx="16" cy="18" r="2" {...stroke} /></>),
  /** See alternatives (the list of routes under a trip) */
  layers: svg(<path d="M12 3l9 5-9 5-9-5 9-5zM3 13l9 5 9-5M3 17l9 5 9-5" {...stroke} />),
  /** Open / see details */
  open: svg(<path d="M7 17L17 7M9 7h8v8" {...stroke} />),
  /** Create copy */
  copy: svg(<><rect x="9" y="9" width="12" height="12" rx="2" {...stroke} /><path d="M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1" {...stroke} /></>),
  /** Set as main */
  star: svg(<path d="M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8L3.5 9.7l5.9-.9L12 3.5z" {...stroke} />),
  /** Move under another trip */
  move: svg(<path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7zM11 14l3-3-3-3" {...stroke} />),
  /** Replace (swap this stop for another city) */
  swap: svg(<path d="M4 8h13l-3-3M20 16H7l3 3" {...stroke} />),
  /** Share (trip sharing v1: open the share dialog) */
  share: svg(<path d="M12 3v12M12 3l-4 4M12 3l4 4M5 13v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6" {...stroke} />),
  /** Delete */
  trash: svg(<path d="M5 7h14M10 11v5M14 11v5M6 7l1 12a2 2 0 002 2h6a2 2 0 002-2l1-12M9 7V4h6v3" {...stroke} />),
} as const;

const KebabIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle cx="12" cy="5" r="1.6" fill="currentColor" />
    <circle cx="12" cy="12" r="1.6" fill="currentColor" />
    <circle cx="12" cy="19" r="1.6" fill="currentColor" />
  </svg>
);

const TONE_COLOR: Record<KebabTone, string> = {
  accent: '#157a55',
  danger: '#cf5a3c',
  muted: '#6c7872',
};

const MenuRow: React.FC<{ item: KebabItem; onDone: () => void }> = ({ item, onDone }) => {
  const [hover, setHover] = useState(false);
  const color = TONE_COLOR[item.tone || 'accent'];
  return (
    <button
      type="button"
      role="menuitem"
      disabled={item.disabled}
      onClick={() => { onDone(); item.onSelect(); }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 8, width: '100%',
        padding: '8px 12px', border: 'none', borderRadius: 8,
        cursor: item.disabled ? 'default' : 'pointer',
        opacity: item.disabled ? 0.4 : 1,
        background: hover && !item.disabled ? 'rgba(20,40,30,0.05)' : 'transparent',
        color, fontSize: 14, fontWeight: 600, textAlign: 'left', whiteSpace: 'nowrap',
      }}
    >
      {item.icon}
      {item.label}
    </button>
  );
};

/** Breathing room to the viewport edge, and the gap under the trigger. */
const MENU_MARGIN = 8;
const MENU_GAP = 4;
/**
 * Above BottomSheet (2500) so the stop sheet's own menu is visible, below
 * ConfirmationDialog / NameDialog (3000) so a confirm still stacks on top.
 */
const MENU_Z_INDEX = 2600;

const KebabMenu: React.FC<KebabMenuProps> = ({ items, disabled, ariaLabel = 'More options' }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const visible = items.filter((i) => !i.hidden);

  // Right-align the menu with the trigger by default (it hangs leftwards, which
  // suits a ⋮ at a card's top-right). Near the left edge — a chip at the start
  // of the scroller — that would run off-screen, so flip to opening rightwards.
  // Measured, since the width depends on the longest label.
  const place = () => {
    const b = btnRef.current;
    const m = menuRef.current;
    if (!b) return;
    const r = b.getBoundingClientRect();
    const menuW = m?.offsetWidth ?? 0;
    const menuH = m?.offsetHeight ?? 0;
    let left = r.right - menuW;
    if (left < MENU_MARGIN) left = r.left;
    left = Math.max(MENU_MARGIN, Math.min(left, window.innerWidth - menuW - MENU_MARGIN));
    let top = r.bottom + MENU_GAP;
    if (menuH > 0 && top + menuH > window.innerHeight - MENU_MARGIN && r.top - MENU_GAP - menuH >= MENU_MARGIN) {
      top = r.top - MENU_GAP - menuH;
    }
    setPos({ top, left });
  };

  // Two passes: the first renders the menu hidden so it can be measured, the
  // second places it. Both run before paint, so there is no visible jump.
  useLayoutEffect(() => {
    if (!open) { setPos(null); return; }
    place();
  }, [open, visible.length]);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const onDown = (e: MouseEvent) => {
      if (menuRef.current?.contains(e.target as Node)) return;
      if (btnRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    // Capture phase + stopImmediatePropagation so Escape closes only the menu:
    // inside a BottomSheet the sheet also listens for Escape, and both firing
    // would dismiss the sheet along with the menu.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopImmediatePropagation();
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey, true);
    // Any scroll (the chip scroller included, hence capture) or resize detaches
    // the fixed menu from its trigger — just close it.
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey, true);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  if (visible.length === 0) return null;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 28, height: 28, borderRadius: '50%', padding: 0,
          border: 'none', background: open ? 'rgba(20,40,30,0.08)' : 'rgba(255,255,255,0.9)',
          color: '#6c7872', cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.4 : 1,
          boxShadow: '0 1px 3px rgba(20,40,30,0.12)',
        }}
      >
        <KebabIcon />
      </button>
      {open && createPortal(
        <div
          ref={menuRef}
          role="menu"
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'fixed',
            top: pos?.top ?? 0,
            left: pos?.left ?? 0,
            // Hidden for the measuring pass only.
            visibility: pos ? 'visible' : 'hidden',
            zIndex: MENU_Z_INDEX,
            minWidth: 152, padding: 4, background: '#ffffff', borderRadius: 12,
            border: '1px solid rgba(20,40,30,0.08)', boxShadow: '0 8px 28px rgba(20,40,30,0.18)',
          }}
        >
          {visible.map((item, i) => (
            <MenuRow key={`${item.label}-${i}`} item={item} onDone={() => setOpen(false)} />
          ))}
        </div>,
        document.body,
      )}
    </>
  );
};

export default KebabMenu;
